import { Card, cn, LoadingOverlay } from '@skupreme/shared/ui';
import { useInventoryStatusHook } from '@hooks/prediction';
import { DateUtility } from '@utility/date';
import StorageProviderService from '@utility/services/storage-provider';
import { ToastMessage } from '@utility/toast';
import {
  type MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  DatesBar,
  differenceInDays,
  getDarkColor,
  ShipmentsSection,
} from '../../legacy-bridge';
import { type TimelineShipment, toTimelineShipments } from '../../lib/inbound';

const BAR_HEIGHT = 23;
const DAY_WIDTH = 30;

/** Days that fit the chart's width, so the timeline always fills it. */
const visibleDays = (chart: HTMLDivElement | null) =>
  chart ? Math.round(chart.clientWidth / DAY_WIDTH) : 0;

/** The provider's inbound shipments on a timeline, dragged sideways. */
export function InboundShipmentsTab({ providerId }: { providerId: string }) {
  const chartRef = useRef<HTMLDivElement>(null);
  const [shipments, setShipments] = useState<{ shipments: unknown[] }>({
    shipments: [],
  });
  const [loading, setLoading] = useState(true);
  const { checkEntry } = useInventoryStatusHook(true);

  const [drag, setDrag] = useState<{ startX: number; scrollLeft: number }>();
  const [minDate, setMinDate] = useState<Date | null>(null);
  const [maxDate, setMaxDate] = useState<Date | null>(null);
  const [rendered, setRendered] = useState(false);
  const [staticColors, setStaticColors] = useState<Record<string, string>>({});
  const [shipmentUserValue, setShipmentUserValue] = useState({});
  const [filteredShipments, setFilteredShipments] = useState([]);
  const [activeRow, setHoverRow] = useState(null);

  useEffect(() => {
    if (!providerId) return;
    let isCurrent = true;
    setLoading(true);
    StorageProviderService.getShipments(providerId)
      .then(({ data }: { data?: TimelineShipment[] }) => {
        if (isCurrent) setShipments({ shipments: toTimelineShipments(data) });
      })
      .catch(error => ToastMessage.apiError(error))
      .finally(() => {
        if (isCurrent) setLoading(false);
      });
    return () => {
      isCurrent = false;
    };
  }, [providerId]);

  /* The last day drawn is never before the chart's right edge. */
  const extendMaxDate = useCallback((date: Date | null) => {
    if (!date) return;
    const minDays = visibleDays(chartRef.current);
    setMaxDate(
      DateUtility.difference(new Date(), date) < minDays
        ? DateUtility.addDays(new Date(), minDays)
        : date,
    );
  }, []);

  useEffect(() => {
    const resize = () => extendMaxDate(maxDate);
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [extendMaxDate, maxDate]);

  /* Opens on today rather than on the first shipment's day. */
  useEffect(() => {
    if (!minDate || !rendered || !chartRef.current) return;
    const daysInPast = differenceInDays(minDate, new Date());
    if (daysInPast > 0) {
      chartRef.current.scrollLeft = daysInPast * DAY_WIDTH;
    }
  }, [minDate, rendered]);

  const getBarColor = useCallback(
    (id: string) => {
      if (staticColors[id]) return staticColors[id];
      const color = getDarkColor();
      if (id) setStaticColors(colors => ({ ...colors, [id]: color }));
      return color;
    },
    [staticColors],
  );

  const startDrag = (event: MouseEvent<HTMLDivElement>) => {
    const chart = chartRef.current;
    if (!chart) return;
    setDrag({
      startX: event.pageX - chart.offsetLeft,
      scrollLeft: chart.scrollLeft,
    });
  };

  const moveDrag = (event: MouseEvent<HTMLDivElement>) => {
    const chart = chartRef.current;
    if (!drag || !chart) return;
    event.preventDefault();
    const x = event.pageX - chart.offsetLeft;
    chart.scrollLeft = drag.scrollLeft - (x - drag.startX) * 2;
  };

  const endDrag = () => setDrag(undefined);

  return (
    <Card bodyClassName="sk:relative sk:p-0">
      {loading ? <LoadingOverlay label="Loading shipments" /> : null}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: drag to scroll; the chart also scrolls natively. */}
      <div
        ref={chartRef}
        onMouseDown={startDrag}
        onMouseMove={moveDrag}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        className={cn(
          'sk:relative sk:min-h-60 sk:min-w-full sk:select-none sk:overflow-y-hidden sk:scroll-smooth',
          drag ? 'sk:cursor-grabbing' : 'sk:cursor-grab',
        )}
      >
        <DatesBar startDate={minDate} endDate={maxDate} DayWidth={DAY_WIDTH} />
        <ShipmentsSection
          data={shipments}
          shipments={filteredShipments}
          filteredShipments={filteredShipments}
          setFilteredShipments={setFilteredShipments}
          checked={shipmentUserValue}
          setChecked={setShipmentUserValue}
          markRendered={() => setRendered(true)}
          setMinDate={setMinDate}
          setMaxDate={extendMaxDate}
          setPredictedShipment={() => {}}
          getBarColor={getBarColor}
          HeightBar={BAR_HEIGHT}
          DayWidth={DAY_WIDTH}
          minDate={minDate}
          activeRow={activeRow}
          setHoverRow={setHoverRow}
          differenceInDates={
            minDate && maxDate ? DateUtility.difference(minDate, maxDate) : 0
          }
          checkEntry={checkEntry}
          shipmentUserValue={shipmentUserValue}
          showShipmentCheckbox
          hidePOPrediction
          onlyShipments
        />
      </div>
    </Card>
  );
}
